import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Table, Tooltip } from 'antd'
import styled from 'styled-components'
import _ from 'lodash'
import { colours } from '@qflow/theme'

import { useProductGroupsLoader } from 'portal/components/RecordsModules/common'
import { imperial } from 'portal/lib/functions'
import { ColourButton, icons } from 'portal/lib/primitives'

import { AddNewThreshold } from './AddNewThreshold'
import { AlertRecipients } from './AlertRecipients'
import {
  AdditionalText,
  AlertRuleContainer,
  Description,
  SettingArea,
  TextArea
} from './sharedStyledComponents'
import { EnableAlertRuleSwitch } from './EnableSwitch'

export const MaterialThresholds = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules,
  cannotEnable
}) => {
  const { error: productGroupsError } = useProductGroupsLoader(projectId)
  if (productGroupsError) {
    console.error('Error loading product groups:', productGroupsError)
  }

  const projectCountry = useSelector(
    (state: any) => state.projectDetails.modules?.country
  )
  const allProductGroups = useSelector((state: any) => state.productGroups)

  const isImperial = useMemo(() => imperial(projectCountry), [projectCountry])
  const WEIGHT_UNIT = useMemo(() => (isImperial ? 'lbs' : 't'), [isImperial])
  const VOLUME_UNIT = useMemo(
    () =>
      isImperial ? (
        'cu yd'
      ) : (
        <span>
          m<sup>3</sup>
        </span>
      ),
    [isImperial]
  )

  const DELIVERY = 'delivery'

  const columns = useMemo(
    () => [
      {
        title: 'Module',
        dataIndex: 'module',
        key: 'module',
        render: (text: any) => (
          <div style={{ margin: '0.375rem -0.25rem 0 0.25rem' }}>
            {text === DELIVERY ? (
              <icons.Modules.Delivery />
            ) : (
              <icons.Modules.Waste colour={'currentColor'} />
            )}
          </div>
        ),
        width: '2rem'
      },
      {
        title: 'Product Group/Waste Code',
        dataIndex: 'productGroup',
        key: 'productGroup',
        render: (text: any, record: any) =>
          record.module === DELIVERY
            ? allProductGroups.data && Array.isArray(allProductGroups.data)
              ? allProductGroups.data.find(item => item.productGroupId === text)
                  .name
              : 'loading...'
            : record.wasteCode
      },
      {
        title: 'Weight',
        dataIndex: 'weight',
        key: 'weight',
        render: (text: any) =>
          text ? (
            <span>
              {text} {WEIGHT_UNIT}
            </span>
          ) : (
            ''
          ),
        width: '25%'
      },
      {
        title: 'Volume',
        dataIndex: 'volume',
        key: 'volume',
        render: (text: any) =>
          text ? (
            <span>
              {text} {VOLUME_UNIT}
            </span>
          ) : (
            ''
          ),
        width: '25%'
      },
      {
        title: '',
        key: 'actions',
        render: (text: any, record: any) => (
          <div style={{ marginRight: '-0.25rem' }}>
            <Tooltip title="Remove threshold">
              <ColourButton.BasicBorderless
                name={`remove-threshold-${record.module}-${record.productGroup?.replace(/\s+/g, '-').toLowerCase()}-${record.wasteCode?.replace(/\s+/g, '-').toLowerCase()}`}
                aria-label={'Remove threshold'}
                style={{
                  padding: '0.25rem 9px',
                  borderRadius: '50%',
                  color: colours.RED_DARKMODE
                }}
                onClick={() => {
                  const updatedAlertRules = _.cloneDeep(alertRules)
                  updatedAlertRules[alertKey].thresholds = updatedAlertRules[
                    alertKey
                  ].thresholds.filter(s => !_.isEqual(s, record))
                  setAlertRules(updatedAlertRules)
                }}
              >
                <icons.TrashCan colour="currentColor" />
              </ColourButton.BasicBorderless>
            </Tooltip>
          </div>
        ),
        width: '2rem'
      }
    ],
    [
      VOLUME_UNIT,
      WEIGHT_UNIT,
      alertKey,
      alertRules,
      allProductGroups,
      setAlertRules
    ]
  )

  const handleAddThreshold = (value: any[]) => {
    if (!value) return

    const updatedAlertRules = _.cloneDeep(alertRules)

    updatedAlertRules[alertKey] = {
      ...updatedAlertRules[alertKey]
    }
    updatedAlertRules[alertKey].thresholds.push(value)

    // Sort watchedMaterials alphabetically
    updatedAlertRules[alertKey].thresholds.sort((a, b) => {
      if (a.module !== b.module) {
        return a.module.localeCompare(b.module)
      }
      if (
        a.module === DELIVERY &&
        b.module === DELIVERY &&
        a.productGroup !== b.productGroup
      ) {
        const pgA = allProductGroups.data.find(
          item => item.productGroupId === a.productGroup
        )
        const pgB = allProductGroups.data.find(
          item => item.productGroupId === b.productGroup
        )
        return pgA.name.localeCompare(pgB.name)
      }
      if (a.wasteCode && b.wasteCode && a.wasteCode !== b.wasteCode) {
        return a.wasteCode.localeCompare(b.wasteCode)
      }
      if (a.weight !== b.weight) {
        return a.weight - b.weight
      }
      if (a.volume !== b.volume) {
        return a.volume - b.volume
      }
      return 0
    })
    setAlertRules(updatedAlertRules)
  }

  if (!alertRules[alertKey]) {
    return null
  }

  return (
    <>
      <AlertRuleContainer>
        <TextArea>
          <Description>Material Thresholds</Description>
          <AdditionalText>
            Get notified when a delivered material or waste&nbsp;collection
            exceeds a set threshold.
          </AdditionalText>
        </TextArea>
        <SettingArea>
          <EnableAlertRuleSwitch
            alertKey={alertKey}
            alertRules={alertRules}
            setAlertRules={setAlertRules}
            cannotEnable={cannotEnable}
            ariaLabel="Enable Material Thresholds"
          />
          <AddNewThreshold
            isImperial={isImperial}
            weightUnit={WEIGHT_UNIT}
            volumeUnit={VOLUME_UNIT}
            onAddThreshold={handleAddThreshold}
          />
          <MaterialThresholdWrapper>
            <StyledTable
              rowKey={(record: any) => record.thresholdId}
              columns={columns}
              dataSource={alertRules[alertKey].thresholds}
              locale={{
                emptyText: (
                  <div>
                    {!alertRules[alertKey].isEnabled ? (
                      'No thresholds set'
                    ) : (
                      <span style={{ color: colours.RED_DARKMODE }}>
                        One or more thresholds required
                      </span>
                    )}
                  </div>
                )
              }}
              pagination={false}
              size="small"
              showHeader={false}
            />
          </MaterialThresholdWrapper>
        </SettingArea>
      </AlertRuleContainer>
      <AlertRecipients
        projectId={projectId}
        alertKey={alertKey}
        alertRules={alertRules}
        setAlertRules={setAlertRules}
      />
    </>
  )
}

const MaterialThresholdWrapper = styled.div`
  margin-top: 1.25rem;
  min-width: 30rem;
`
const StyledTable = styled(Table)`
  tr > td.ant-table-cell-row-hover {
    background-color: ${colours.WHITE} !important;
  }
`
